import { Layers } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatLevel } from '@/lib/utils'
import type { ProximiioFloor } from '@/types/proximi'

export interface FloorOption {
  level: number
  label: string
  /** How many live users are currently on this floor. */
  users: number
}

/**
 * Builds the floor list from the platform's floors, falling back to whatever
 * levels the loaded features actually carry — customer data is not always
 * complete on both sides, and a missing floor record should not hide a floor.
 */
export function buildFloorOptions(
  floors: ProximiioFloor[],
  featureLevels: number[],
  userCounts: Map<number, number>,
): FloorOption[] {
  const byLevel = new Map<number, string>()
  for (const floor of floors) {
    if (!byLevel.has(floor.level)) {
      byLevel.set(floor.level, floor.name?.trim() || `Level ${formatLevel(floor.level)}`)
    }
  }
  for (const level of featureLevels) {
    if (!byLevel.has(level)) byLevel.set(level, `Level ${formatLevel(level)}`)
  }

  return [...byLevel.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([level, label]) => ({ level, label, users: userCounts.get(level) ?? 0 }))
}

export function FloorSwitcher({
  options,
  level,
  onLevelChange,
}: {
  options: FloorOption[]
  level: number | null
  onLevelChange: (level: number) => void
}) {
  if (options.length === 0) return null

  return (
    <Select
      value={level === null ? undefined : String(level)}
      onValueChange={(value) => onLevelChange(Number(value))}
    >
      <SelectTrigger size="sm" className="w-full bg-card" aria-label="Floor">
        <Layers className="text-muted-foreground" />
        <SelectValue placeholder="Select floor" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.level} value={String(option.level)}>
            <span className="flex w-full items-center justify-between gap-3">
              <span className="truncate">{option.label}</span>
              {option.users > 0 && (
                <span className="text-muted-foreground text-xs tabular-nums">{option.users}</span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
